// Angular
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, HostListener, Inject, AfterContentInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// import { ConnectionService } from 'ng-connection-service';
// Layout
import { LayoutConfigService, SplashScreenService } from './core/_base/layout';
import { LayoutUtilsService, MessageType } from "./core/_base/crud";
// Metronic
import { TranslationService } from './core/_base/metronic';
import { UserService } from './core/auth/_services/user.service';
// language list
import { locale as enLang } from './core/_config/i18n/en';
import { locale as chLang } from './core/_config/i18n/ch';
import { locale as esLang } from './core/_config/i18n/es';
import { locale as jpLang } from './core/_config/i18n/jp';
import { locale as deLang } from './core/_config/i18n/de';
import { locale as frLang } from './core/_config/i18n/fr';
import { locale as ptLang } from './core/_config/i18n/pt';
// import { ConnectionService } from './core/_base/connection-service/connection-service';
import { Observable, Observer, fromEvent, merge, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { DOCUMENT } from '@angular/common';

@Component({
	selector: 'body[kt-root]',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy, AfterContentInit {
	// Public properties
	title = 'Uptime AI';
	loader: boolean;
	isConnected: boolean = true;
	oldDate: any = null;
	newDate: any = null;
	private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param translationService: TranslationService
	 * @param router: Router
	 * @param layoutConfigService: LayoutCongifService
	 * @param splashScreenService: SplashScreenService
	 */
	constructor(
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly layoutConfigService: LayoutConfigService,
		private readonly splashScreenService: SplashScreenService,
		private readonly userService: UserService,
		// private readonly connectionService: ConnectionService,
		private readonly layoutUtilsService: LayoutUtilsService,
		// private readonly connectionService: ConnectionService
		@Inject(DOCUMENT) private document: Document,
	) {
		this.initService();

		if (environment.runtime.SUBSCRIPTION_EXPIRED && environment.runtime.SUBSCRIPTION_EXPIRY_TYPE == 2)
			this.showSubscriptionExpiryMessage();

		this.createOnline$().subscribe(connected => {
			this.isConnected = connected;
			if (!this.loader)
				return
			if (this.isConnected) {
				const _saveMessage = "You're Online";
				this.layoutUtilsService.showActionNotification(_saveMessage, MessageType.Create, 10000);
			} else {
				this.isConnected = connected;
				const _saveMessage = "You're Offline";
				this.layoutUtilsService.showActionNotification(_saveMessage, MessageType.Create, 10000);
			}
			this.loader = true
		});
		// register translations
		this.translationService.loadTranslations(enLang, chLang, esLang, jpLang, deLang, frLang, ptLang);
		this.translationService.setLanguage(this.translationService.getSelectedLanguage());
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	// call this event handler before browser refresh
	@HostListener("window:visibilitychange") unMountLastActiveTime() {
		if (document.hidden && this.userService.getLoggedInUser()) {
			this.newDate = new Date();
			this.updateUsageHours();
			return;
		}

		if (this.userService.getLoggedInUser()) {
			this.oldDate = new Date();
			this.userService.updateUserLastActivityTime({ lastActiveAt: new Date().toISOString() });
		}
	}

	updateUsageHours() {
		if (this.oldDate && this.newDate) {
			const diffMs = (this.newDate - this.oldDate);
			const diffHrs = (diffMs % 86400000) / 3600000; // hours
			const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
			let activeHours = Number.parseFloat((diffHrs + (diffMins/60)).toFixed(4));
			this.userService.updateUserLastActivityTime({ lastActiveAt: new Date().toISOString(), activeHours });
		}
	}

	showSubscriptionExpiryMessage() {
		let expiryMessage = "License is under grace period and application access shall be lost on Mar 24, 2024 due to license expiry. Please contact administrator for license extension.";
		this.layoutUtilsService.showActionNotification(expiryMessage, MessageType.Create, 100000);
	}

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.oldDate = new Date();
		// enable/disable loader
		const googleFont = document.getElementById("google-font")
		if (environment.runtime.onPremDeployment) {
			googleFont.setAttribute('href', './assets/vendors/base/style.scss')
		}
		else {
			googleFont.setAttribute('href', '//fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap')
		}

		this.loader = this.layoutConfigService.getConfig('loader.enabled');

		const routerSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				// hide splash screen
				this.splashScreenService.hide();

				// scroll to top on every route change
				window.scrollTo(0, 0);
			}
		});
		this.unsubscribe.push(routerSubscription);
	}

	createOnline$() {
		return merge(
			fromEvent(window, 'offline').pipe(map(() => false)),
			fromEvent(window, 'online').pipe(map(() => true)),
			new Observable((sub: Observer<boolean>) => {
				sub.next(navigator.onLine);
				sub.complete();
			}));
	}

	initService() {
		const conn = (navigator as any).connection;
		if (conn && conn.downlink < 0.8) {
			let msg = "You have a slow internet connection, page will take while to load"
			this.layoutUtilsService.showActionNotification(msg, MessageType.Create, 4000)
		}
	}
	/**
	 * On Destroy
	 */
	ngOnDestroy() {
		this.unsubscribe.forEach(sb => sb.unsubscribe());
	}

	ngAfterContentInit() {
		if (environment.runtime.ENABLE_HOTJAR && environment.runtime.HOTJAR_KEY != '') {
			((h, o, t, j, a, r) => {
				h.hj =
					h.hj ||
					function () {
						(h.hj.q = h.hj.q || []).push(arguments);
					};
				h._hjSettings = { hjid: environment.runtime.HOTJAR_KEY, hjsv: 6 };
				a = o.getElementsByTagName('head')[0];
				r = o.createElement('script'); r.async = 1;
				r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
				a.appendChild(r);
			})(window as any, this.document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
		}

		if (environment.runtime.ENABLE_HEAP && environment.runtime.HEAP_KEY != '') {
			(function (window: any, document: Document, appId: string) {
				window.heapReadyCb = window.heapReadyCb || [];
				window.heap = window.heap || [];

				window.heap.load = function (e: string, t?: any) {
					window.heap.envId = e;
					window.heap.clientConfig = t = t || {};
					window.heap.clientConfig.shouldFetchServerConfig = false;

					const script = document.createElement('script');
					script.type = 'text/javascript';
					script.async = true;
					script.src = `https://cdn.us.heap-api.com/config/${e}/heap_config.js`;

					const firstScript = document.getElementsByTagName('script')[0];
					if (firstScript?.parentNode) {
						firstScript.parentNode.insertBefore(script, firstScript);
					} else {
						document.head.appendChild(script);
					}

					const methods = [
						"init", "startTracking", "stopTracking", "track", "resetIdentity", "identify",
						"getSessionId", "getUserId", "getIdentity", "addUserProperties", "addEventProperties",
						"removeEventProperty", "clearEventProperties", "addAccountProperties", "addAdapter",
						"addTransformer", "addTransformerFn", "onReady", "addPageviewProperties",
						"removePageviewProperty", "clearPageviewProperties", "trackPageview"
					];

					const makeStub = function (method: string) {
						return function (...args: any[]) {
							window.heapReadyCb.push({
								name: method,
								fn: function () {
									if (window.heap[method]) {
										window.heap[method].apply(window.heap, args);
									}
								}
							});
						};
					};

					for (let i = 0; i < methods.length; i++) {
						window.heap[methods[i]] = makeStub(methods[i]);
					}
				};

				window.heap.load(appId);
			})(window, this.document, environment.runtime.HEAP_KEY);
		}
	}
}
